/**
 * 后台 — 提现审核
 * GET    /api/admin/withdrawals
 * POST   /api/admin/withdrawals/:id/approve
 * POST   /api/admin/withdrawals/:id/reject
 * POST   /api/admin/withdrawals/:id/paid
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DollarSign, CheckCircle2, XCircle, Loader2, Search, Filter,
  Eye, X, Shield, User, Clock, AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { api } from '../../lib/api.js';

const STATUS = {
  pending: { label: '待审核', color: 'text-accent-warn', bg: 'bg-accent-warn/10', border: 'border-accent-warn/30' },
  approved: { label: '已批准', color: 'text-brand-cyan', bg: 'bg-brand-cyan/10', border: 'border-brand-cyan/30' },
  paid: { label: '已打款', color: 'text-accent-success', bg: 'bg-accent-success/10', border: 'border-accent-success/30' },
  rejected: { label: '已拒绝', color: 'text-accent-danger', bg: 'bg-accent-danger/10', border: 'border-accent-danger/30' },
};

const PAY_METHOD = {
  alipay: { label: '支付宝', icon: '💙' },
  wechat: { label: '微信', icon: '💚' },
  bank: { label: '银行卡', icon: '💳' },
};

export function WithdrawalsView() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);  // 当前查看的提现
  const [action, setAction] = useState(null);  // approve / reject
  const [notes, setNotes] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.admin.withdrawals(filter);
      setList(r.withdrawals || []);
    } catch (e) {
      toast.error('加载失败: ' + e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [filter]);

  const handleAction = async (w, act) => {
    setDetail(w);
    setAction(act);
    setNotes('');
  };

  const submitAction = async () => {
    if (!detail || !action) return;
    try {
      let r;
      if (action === 'approve') r = await api.admin.approveWithdrawal(detail.id, notes);
      else if (action === 'reject') r = await api.admin.rejectWithdrawal(detail.id, notes || '不符合提现规则');
      else if (action === 'paid') r = await api.admin.markWithdrawalPaid(detail.id, notes);
      if (r?.ok) {
        toast.success(r.message || '操作成功');
        setDetail(null);
        setAction(null);
        load();
      } else {
        toast.error(r?.error || '操作失败');
      }
    } catch (e) {
      toast.error('操作失败: ' + e.message);
    }
  };

  const filtered = list.filter(w => {
    if (!search) return true;
    const q = search.toLowerCase();
    return w.id == q
      || (w.user_phone || '').includes(q)
      || (w.real_name || '').includes(q)
      || (w.pay_account || '').includes(q);
  });

  // 统计
  const stats = {
    pending: list.filter(w => w.status === 'pending').length,
    approved: list.filter(w => w.status === 'approved').length,
    paid: list.filter(w => w.status === 'paid').length,
    total_amount: list.filter(w => w.status === 'pending' || w.status === 'approved').reduce((s, w) => s + (+w.amount || 0), 0),
  };

  return (
    <div className="space-y-3">
      {/* 统计卡 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatCard label="待审核" value={stats.pending} color="text-accent-warn" bg="bg-accent-warn/10" />
        <StatCard label="已批准" value={stats.approved} color="text-brand-cyan" bg="bg-brand-cyan/10" />
        <StatCard label="已打款" value={stats.paid} color="text-accent-success" bg="bg-accent-success/10" />
        <StatCard label="待打款金额" value={`¥${stats.total_amount.toFixed(0)}`} color="text-brand-gold" bg="bg-brand-gold/10" small />
      </div>

      {/* 筛选 + 搜索 */}
      <div className="card !p-2 flex items-center gap-2 flex-wrap">
        {[
          { v: 'pending', label: '待审核' },
          { v: 'approved', label: '已批准' },
          { v: 'paid', label: '已打款' },
          { v: 'rejected', label: '已拒绝' },
          { v: 'all', label: '全部' },
        ].map(t => (
          <button
            key={t.v}
            onClick={() => setFilter(t.v)}
            className={clsx(
              'px-2.5 py-1 rounded text-xs border transition',
              filter === t.v
                ? 'bg-brand-cyan/20 border-brand-cyan/40 text-brand-cyan'
                : 'bg-white/5 border-white/10 text-ink-2'
            )}
          >
            {t.label}
          </button>
        ))}
        <div className="flex-1 flex items-center gap-1 px-2 py-1 bg-bg-base rounded border border-white/10 min-w-[160px]">
          <Search size={12} className="text-ink-3" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="搜索手机号/姓名/账号"
            className="flex-1 bg-transparent text-xs outline-none"
          />
        </div>
        <button onClick={load} className="px-2 py-1 text-xs text-ink-3 hover:text-brand-cyan flex items-center gap-1">
          <Loader2 size={11} className={loading ? 'animate-spin' : ''} /> 刷新
        </button>
      </div>

      {/* 列表 */}
      {loading ? (
        <div className="card text-center py-8">
          <Loader2 size={20} className="mx-auto animate-spin text-brand-cyan" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-12 text-ink-3 text-sm">
          <DollarSign size={32} className="mx-auto mb-2 opacity-30" />
          暂无 {filter === 'all' ? '' : STATUS[filter]?.label || filter} 提现申请
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(w => (
            <WithdrawalCard key={w.id} w={w} onAction={handleAction} />
          ))}
        </div>
      )}

      {/* 详情 / 操作 Modal */}
      <AnimatePresence>
        {detail && action && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-3"
            onClick={() => { setDetail(null); setAction(null); }}
          >
            <motion.div
              initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
              className="card max-w-md w-full"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="text-base font-bold flex items-center gap-2">
                  {action === 'approve' ? <><CheckCircle2 size={16} className="text-brand-cyan" /> 批准提现</> :
                   action === 'reject' ? <><XCircle size={16} className="text-accent-danger" /> 拒绝提现</> :
                   <><DollarSign size={16} className="text-accent-success" /> 标记已打款</>}
                </div>
                <button onClick={() => { setDetail(null); setAction(null); }} className="p-1 rounded hover:bg-white/5">
                  <X size={16} />
                </button>
              </div>
              <div className="space-y-2 text-xs">
                <div className="p-2 rounded bg-bg-base/50">
                  <div className="text-ink-3 mb-1">申请用户</div>
                  <div className="flex items-center gap-2">
                    <User size={12} className="text-ink-3" />
                    <span>{detail.real_name || detail.user_name}</span>
                    <span className="text-ink-3 font-mono">({detail.user_phone})</span>
                  </div>
                </div>
                <div className="p-2 rounded bg-bg-base/50">
                  <div className="text-ink-3 mb-1">提现金额</div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold text-brand-cyan">¥{detail.amount.toFixed(2)}</span>
                    {detail.fee > 0 && <span className="text-[10px] text-ink-3">(手续费 ¥{detail.fee.toFixed(2)})</span>}
                  </div>
                  <div className="text-[10px] text-ink-3 mt-0.5">实际到账 ¥{detail.actual_amount.toFixed(2)}</div>
                </div>
                <div className="p-2 rounded bg-bg-base/50">
                  <div className="text-ink-3 mb-1">收款方式</div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{PAY_METHOD[detail.pay_method]?.icon}</span>
                    <span>{PAY_METHOD[detail.pay_method]?.label || detail.pay_method}</span>
                    <span className="text-ink-3">·</span>
                    <span className="font-mono">{detail.pay_account}</span>
                  </div>
                  <div className="text-[10px] text-ink-3 mt-1">收款人:{detail.pay_real_name}</div>
                </div>
                <div className="p-2 rounded bg-bg-base/50">
                  <div className="text-ink-3 mb-1">身份证</div>
                  <div className="font-mono">{detail.id_card ? detail.id_card.slice(0, 6) + '******' + detail.id_card.slice(-4) : '未认证'}</div>
                </div>
                <div>
                  <div className="text-[10px] text-ink-3 mb-1">操作备注 {action === 'reject' && <span className="text-accent-danger">*必填</span>}</div>
                  <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder={action === 'reject' ? '请填写拒绝原因' : '选填,内部备注'}
                    className="w-full px-2 py-1.5 bg-bg-base border border-white/10 rounded text-xs outline-none focus:border-brand-cyan/40 resize-none"
                    rows={2}
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => { setDetail(null); setAction(null); }}
                  className="flex-1 py-2 rounded-lg bg-white/5 border border-white/10 text-xs"
                >
                  取消
                </button>
                <button
                  onClick={submitAction}
                  className={clsx(
                    'flex-1 py-2 rounded-lg text-white text-xs font-semibold flex items-center justify-center gap-1.5',
                    action === 'approve' && 'bg-gradient-to-r from-brand-cyan to-brand-purple',
                    action === 'reject' && 'bg-gradient-to-r from-accent-danger to-brand-rose',
                    action === 'paid' && 'bg-gradient-to-r from-accent-success to-brand-cyan',
                  )}
                >
                  {action === 'approve' ? <><CheckCircle2 size={12} /> 确认批准</> :
                   action === 'reject' ? <><XCircle size={12} /> 确认拒绝</> :
                   <><DollarSign size={12} /> 已打款</>}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function StatCard({ label, value, color, bg, small = false }) {
  return (
    <div className={clsx('rounded-lg p-2.5 border border-white/5', bg)}>
      <div className="text-[10px] text-ink-3">{label}</div>
      <div className={clsx(small ? 'text-base' : 'text-2xl', 'font-bold font-mono', color)}>{value}</div>
    </div>
  );
}

function WithdrawalCard({ w, onAction }) {
  const s = STATUS[w.status] || STATUS.pending;
  const pm = PAY_METHOD[w.pay_method] || {};
  const actions = {
    pending: (
      <>
        <button
          onClick={() => onAction(w, 'approve')}
          className="px-2 py-1 text-[10px] rounded bg-brand-cyan/20 border border-brand-cyan/40 text-brand-cyan hover:bg-brand-cyan/30"
        >
          批准
        </button>
        <button
          onClick={() => onAction(w, 'reject')}
          className="px-2 py-1 text-[10px] rounded bg-accent-danger/10 border border-accent-danger/30 text-accent-danger hover:bg-accent-danger/20"
        >
          拒绝
        </button>
      </>
    ),
    approved: (
      <button
        onClick={() => onAction(w, 'paid')}
        className="px-2 py-1 text-[10px] rounded bg-accent-success/20 border border-accent-success/40 text-accent-success hover:bg-accent-success/30"
      >
        标记已打款
      </button>
    ),
  };
  return (
    <div className={clsx('card !p-3 border', s.border)}>
      <div className="flex items-start gap-3">
        {/* 用户信息 */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="text-sm font-bold">¥{w.amount.toFixed(2)}</div>
            <span className={clsx('text-[10px] px-1.5 py-0.5 rounded', s.color, s.bg)}>{s.label}</span>
            {w.id_verified ? (
              <span className="text-[9px] px-1 rounded bg-accent-success/10 text-accent-success flex items-center gap-0.5">
                <Shield size={8} /> 已实名
              </span>
            ) : (
              <span className="text-[9px] px-1 rounded bg-accent-danger/10 text-accent-danger">未实名</span>
            )}
          </div>
          <div className="text-[11px] text-ink-2 flex items-center gap-2 flex-wrap">
            <span className="flex items-center gap-0.5">
              <User size={10} />
              {w.real_name || w.user_name}
            </span>
            <span className="text-ink-3 font-mono">{w.user_phone}</span>
            <span className="text-ink-3">·</span>
            <span>{pm.icon} {pm.label || w.pay_method}</span>
            <span className="font-mono text-ink-2">{w.pay_account}</span>
          </div>
          <div className="text-[10px] text-ink-3 mt-1 flex items-center gap-2 flex-wrap">
            <span className="flex items-center gap-0.5">
              <Clock size={9} />
              {new Date(w.created_at).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
            </span>
            {w.reviewed_by && <span>· 审核人:{w.reviewed_by}</span>}
            {w.paid_at && <span className="text-accent-success">· 已打款 {new Date(w.paid_at).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit' })}</span>}
          </div>
          {w.reject_reason && (
            <div className="text-[10px] text-accent-danger mt-1">拒绝原因:{w.reject_reason}</div>
          )}
          {w.notes && (
            <div className="text-[10px] text-ink-3 mt-0.5">备注:{w.notes}</div>
          )}
        </div>
        {/* 操作 */}
        <div className="flex flex-col gap-1.5 flex-shrink-0">
          {actions[w.status]}
        </div>
      </div>
    </div>
  );
}
